import { DecimalPipe, PercentPipe } from '@angular/common';
import { Component, DestroyRef, ElementRef, HostListener, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Params } from '@angular/router';
import {
  ChipComponent,
  DropdownMenuComponent,
  GridCorrelationRendererComponent,
  GridLabelTagRendererComponent,
  GridModule,
  GridRadioSelectionComponent,
  LoaderModule
} from '@mds/ui';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { MakersiteService } from '@services/makersite.service';
import { GridOverrides, OrdinalDatePipe } from '@mds/common';
import { ColDef, GridApi, RowNode } from 'ag-grid-community';
import Highcharts from 'highcharts';
import { HighchartsChartModule } from 'highcharts-angular';
import { MenuService } from '@services/menu.service';
import { getEntityTypeIcon } from '@utils/entity-type-icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { finalize } from 'rxjs';

const DEFAULT_COLORS = ['#DADBDC', '#B5B4B5', '#969496', '#7A7879', '#616061', '#4B4B4C', '#37383A'];
const ALTERNATIVE_COLORS = ['#b0eee7', '#01c9b3', '#00747a', '#003c4f', '#003133', '#018f7f', '#b0c3c8'];

@Component({
  selector: 'app-ecodesign',
  standalone: true,
  templateUrl: './ecodesign.component.html',
  styleUrls: ['./ecodesign.component.scss'],
  providers: [DecimalPipe, PercentPipe],
  imports: [
    ChipComponent,
    DropdownMenuComponent,
    MdsIconsModule,
    GridModule,
    HighchartsChartModule,
    LoaderModule,
    MatTooltipModule,
    OrdinalDatePipe
  ]
})
export class EcodesignComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private route = inject(ActivatedRoute);
  private makersiteService = inject(MakersiteService);
  private decimalPipe = inject(DecimalPipe);
  private percentPipe = inject(PercentPipe);
  private menuService = inject(MenuService);

  protected readonly icons = MdsIcon;
  protected gridApi: GridApi;
  protected overrides: GridOverrides;
  rid: string;
  reload: boolean;
  protected isLoading = true;
  protected chart1Layer: any;
  protected chart2Layer: any;
  protected Highcharts = Highcharts;
  protected Chart1Options: Highcharts.Options;
  protected Chart2Options: Highcharts.Options;
  private chart1: Highcharts.Chart;
  private chart2: Highcharts.Chart;
  chartCallback1!: Highcharts.ChartCallbackFunction;
  chartCallback2!: Highcharts.ChartCallbackFunction;

  data: any;
  productInfo: any;
  errorMsg: any;
  layers: { text: string; value: string; key: string; label: string; unit: string }[] = [];
  headers: any;
  chartDropDown = [];

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
   this.reflowCharts();
  }

  constructor() {
    this.chartCallback1 = (chart) => {
      this.chart1 = chart;
    };

    this.chartCallback2 = (chart) => {
      this.chart2 = chart;
    };
  }

  ngOnInit(): void {
    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: Params) => {
      this.rid = params.get('rid');
      this.init();
    });
    this.subscribeToReload();
  }

  onGridReady(gridApi: GridApi) {
    this.gridApi = gridApi;

    gridApi?.forEachNode((node: RowNode) => {
      if (node.childrenAfterGroup.length) this.assignFirstNodeSelected(node);
    });
    setTimeout(() => {
      this.drawChart();
    }, 100);
  }

  onChartChange([layer], type) {
    const rootLevelNodes = [];
    this.gridApi.forEachNode((node) => {
      if (node.level === 1) rootLevelNodes.push(node.data);
    });
    const chartData = [];
    rootLevelNodes.forEach((node, index) => {
      const { name, impacts } = node;
      chartData.push({
        name,
        data: [
          {
            y: impacts[layer.key].default,
            color: DEFAULT_COLORS[index]
          },
          {
            y: impacts[layer.key].alternative || impacts[layer.key].default,
            color: ALTERNATIVE_COLORS[index]
          }
        ]
      });
    });
    if (type === 'chart1') {
      this.Chart1Options = this.getChartOptions(chartData);
      const index = layer.label.indexOf('[');
      this.chart1Layer = {
        title: layer.label.replace(/\[.*?\]/g, ''),
        subtext: `[${layer.unit}/${this.productInfo.unit}]`
      };
    } else if (type === 'chart2') {
      this.Chart2Options = this.getChartOptions(chartData);
      const index = layer.label.indexOf('[');
      this.chart2Layer = {
        title: layer.label.replace(/\[.*?\]/g, ''),
        subtext: `[${layer.unit}/${this.productInfo.unit}]`
      };
    }
  }

  init() {
    console.log(this.rid, 'thisrid');
    this.reset();
    this.isLoading = true;
    this.makersiteService
      .getEcodesignData(this.rid, 'module=mattermaps&layer=ecodesign')
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res: any) => {
          this.reload = false;
          this.productInfo = res.product_info;
          this.headers = res.header.map((header) => {
            if (header.key == 'gwp') {
              header.key = 'footprint';
            } else if (header.key == 'mat_cost') {
              header.key = 'price';
            }
            header.text = header.label;
            header.value = header.label;
            return header;
          });
          this.initGrid(res);
        },
        error: (error) => {
          if (error.status === 400 && error.error.message) {
            this.errorMsg = error.error;
            this.reload = true;
          }
        }
      });

    this.makersiteService
      .getTileCards(this.rid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          const entity = response[0];
          this.makersiteService.getMattermapLayers().subscribe(
            (result: any) => {
              const layerList = result.layers;
              const app = (layerList.find((e) => e.category === 'Business').list as Array<any>).find(
                (e) => e.layer === 'ecodesign-dashboard' || e.module === 'ecodesign-dashboard'
              );
              const entityIcons = [{ key: getEntityTypeIcon(entity.stage) }];
              this.menuService.setMenuConfig({
                activeEntity: {
                  mainIcon: {
                    icon: MdsIcon.PRODUCT_GROUP,
                    pathColors: ['#fff']
                  },

                  isDropdown: true,
                  rid: entity?.rid,
                  name: entity?.title,
                  icons: entityIcons,
                  subtitle: entity?.card_type_label
                },
                activeApp: {
                  mainIcon: {
                    icon: MdsIcon.APP_ECODESIGN_DASHBOARD,
                    pathColors: ['']
                  },
                  rid: this.rid,
                  name: app?.app_name,
                  icons: [],
                  subtitle: '',
                  category: '',
                  menuTrigger: 'selectApps'
                }
              });
            },
            (error) => {
              this.isLoading = false;
              if (error.error.message) {
                this.errorMsg = error.error;
                this.reload = true;
              }
            }
          );
        },
        error: (error) => {
          console.error('Error:', error);
          this.isLoading = false;
          if (error.error.message) {
            this.errorMsg = error.error;
            this.reload = true;
          }
        }
      });
  }

  private subscribeToReload() {
    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((reload) => {
      if (reload) {
        this.init();
        this.menuService.setReloadState(false);
      }
    });
  }

  private reset() {
    this.data = null;
    this.productInfo = null;
    this.errorMsg = null;
    this.layers = [];
    this.chartDropDown = [];
  }

  private initGrid(res) {
    // find impacts from the first node which has impacts
    const impacts = res.contributors.find((node) => !!node.impacts)?.impacts || {};
    const impactKeys = Object.keys(impacts);
    // extract Layers from impact lists
    this.layers = res.header.filter((header) => impactKeys.includes(header.key));
    this.chartDropDown.push(...this.layers);

    const headers = res.header
      .map((header) => ({
        field: header.key,
        headerName: header.label + (header.unit ? ` [${header.unit}/${this.productInfo.unit}]` : ''),
        headerTooltip: header.label + (header.unit ? ` [${header.unit}/${this.productInfo.unit}]` : ''),
        minWidth: 160,
        sortable: true,
        floatingFilter: true,
        resizable: true,
        hide: header.is_hidden,
        filter: 'agTextColumnFilter'
      }))
      .filter((header) => header.field !== 'top_Contributors')
      .map((header) => {
        if (header.field === 'status') {
          header.field = 'label';
          header.cellRenderer = GridLabelTagRendererComponent;
          header.cellRendererParams = ({ data }) => {
            const label = data.label || '';
            // style the tags
            return {
              bgcolor: {
                best_footprint: '#B0EEE7',
                best_cost: '#F4FCB0',
                better_footprint: '#FFE4D1',
                better_cost: '#DDE7FB',
                default: '#D9DADB'
              }[label]
              // textcolor: 'black',
            };
          };
          // style text to display
          header.valueGetter = ({ data }) => (data.label || '').replace('_', ' ').replace('best', '☆ best');
        } else if (impactKeys.includes(header.field)) {
          header.cellRenderer = GridCorrelationRendererComponent;
          header.wrapHeaderText = false;
          header.autoHeaderHeight = false;
          header.tooltipValueGetter = ({ data, column }) => {
            if (!data.impacts) return null;
            return data.impacts[column.colId]?.alternative || data.impacts[column.colId]?.default;
          };
          header.cellRendererParams = ({ node, data }) => {
            if (!data.impacts) return {};
            const value = data.impacts[header.field];
            if (value && !value.alternative) {
              return {
                textcolor: '#B3B5B7',
                delta: ''
              };
            }
            if (node.level === 1) {
              // Show correlation differences on Root level nodes.
              return {
                textcolor: value?.correlation === 'negative' ? '#FF3232' : '#248449',
                delta: this.percentPipe.transform(value?.delta / value.default, '0.0-2')
              };
            }
            return {
              delta: ''
            };
          };
          header.valueGetter = ({ data, column }) => {
            if (!data.impacts) return '';
            const value = data.impacts[column.colId]?.alternative || data.impacts[column.colId]?.default;
            // for smaller values, transform into upto 8 decimal digits else min+max 3 decimal digits
            const unit = Math.abs(value) < 1 ? '0.0-8' : '0.3-3';
            return this.decimalPipe.transform(value, unit);
          };
        }
        return header;
      });
    const rows = this.getRows(res.contributors);
    this.overrides = {
      columnDefs: headers,
      rowData: rows,
      defaultColDef: {
        flex: 1,
        minWidth: 100,
        enableValue: true,
        enablePivot: true,
        sortable: true,
        resizable: true,
        floatingFilter: true,
        filter: 'agTextColumnFilter'
      },
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel',
            toolPanelParams: {
              suppressPivotMode: true
            }
          },
          {
            id: 'filters',
            labelDefault: 'Filters',
            labelKey: 'filters',
            iconKey: 'filter',
            toolPanel: 'agFiltersToolPanel'
          }
        ]
      },
      treeGrid: true,
      autoGroupColumnDef: {
        headerName: 'Top Contributors',
        headerTooltip: 'Top Contributors',
        filter: 'agTextColumnFilter',
        field: 'name',
        minWidth: 400,
        floatingFilter: true,
        cellClass: 'top-contributors-cell',
        cellRenderer: 'agGroupCellRenderer',
        cellRendererParams: {
          suppressCount: true,
          innerRenderer: GridRadioSelectionComponent,
          innerRendererParams: ({ data, node }) => ({
            link: data?.data?.link?.url || '',
            radioGroupName: node.data.group,
            isSelected: data.selected,
            isSelectable: data?.parents?.length > 1 && node?.childrenAfterGroup.length < 1,
            onChange: (event) => {
              node.setData({
                ...node.data,
                selected: true
              });
              node.data.selected = true;
              this.updateSelectedAlternatives({ node, data });
              setTimeout(() => {
                this.drawChart();
              }, 100);
            }
          })
        }
      } as ColDef,
      getDataPath: (data) => data.parents,
      groupDefaultExpanded: 2,
      rowClassRules: {
        // add highlight class to Selected Alternative row for css styles
        'ecodesign-selected-row': ({ node, data }) => node.level === 2 && data.selected
      }
    };
  }

  private getRows(nodes) {
    const rowData = [];
    nodes.forEach((node) => {
      const { id, name, geo, parents, level, label, impacts, nid, selected = false } = node;
      const row: any = {
        id,
        name,
        geo,
        impacts,
        parents,
        level,
        label,
        selected
      };
      row.data = node;

      if (parents) {
        row.parents = parents.slice().reverse();
        if (parents.length > 1) {
          // for all child nodes
          row.parent = nodes.find((node) => node.id === row.parents.at(-2));
          row.root = nodes.find((node) => node.id === parents.at(-1));
          // assign group named for child nodes [name] and leaf nodes[id-name]
          row.group = `${impacts ? `${row.root?.id}-` : ''}${row.root?.name}`;
        }
      } else row.parents = [];

      rowData.push(row);
    });
    return rowData;
  }

  private assignFirstNodeSelected(node: RowNode) {
    if (node.childrenAfterGroup.length) {
      const child = node.childrenAfterGroup[0];
      // recursively select 1 child - Depth First
      if (child.childrenAfterGroup.length) this.assignFirstNodeSelected(child);
      child?.parent?.setData({
        ...child.parent.data,
        impacts: child.data.impacts,
        label: child.data.label,
        selected: node.level === 1 || (child.firstChild && child.parent.firstChild)
      });

      // leaf node
      if (child.childrenAfterGroup.length < 1) {
        child.setData({
          ...child.data,
          selected: child.firstChild && child.parent.firstChild
        });
      }
    }
  }

  private updateSelectedAlternatives({ node, data }) {
    if (!node.parent) return;

    // update parent node data to update Grid
    node.parent.setData({
      ...node.parent.data,
      impacts: data.data.impacts, // copy selected values to parent node
      label: data.label,
      selected: true
    });

    // Mark other selected nodes unselected
    Object.values(node.parent.childrenMapped)
      // filter previously selected node
      .filter((childNode: any) => childNode.data.selected && childNode.rowIndex !== node.rowIndex)
      .forEach((childNode: any) => {
        if (childNode.level > 1) {
          // deselect selected node of previous Alternative
          const prevSelected = childNode.childrenAfterGroup.find((node) => node.data.selected);
          if (prevSelected) {
            prevSelected.setData({
              ...prevSelected.data,
              selected: false
            });
          }
          childNode.setData({
            ...childNode.data,
            impacts: childNode.data.impacts,
            selected: false
          });
        }
      });
    // update recursively till root node
    if (node.level > 1) this.updateSelectedAlternatives({ node: node.parent, data });
  }

  private drawChart() {
    const rootLevelNodes = [];

    this.gridApi.forEachNode((node) => {
      if (node.level === 1) rootLevelNodes.push(node.data);
    });

    const chart1Data = [];
    const chart2Data = [];

    rootLevelNodes.forEach((node, index) => {
      const { name, impacts } = node;
      chart1Data.push({
        name,
        data: [
          {
            y: impacts.footprint.default,
            color: DEFAULT_COLORS[index]
          },
          {
            y: impacts.footprint.alternative || impacts.footprint.default,
            color: ALTERNATIVE_COLORS[index]
          }
        ]
      });
      chart2Data.push({
        name,
        data: [
          {
            y: impacts.price.default,
            color: DEFAULT_COLORS[index]
          },
          {
            y: impacts.price.alternative || impacts.price.default,
            color: ALTERNATIVE_COLORS[index]
          }
        ]
      });
    });

    this.headers.forEach((header) => {
      if (header.key == 'footprint' || header.key == 'price') {
      }
    });

    this.chart1Layer = {
      title: this.layers[0].label.replace(/\[.*?\]/g, ''),
      subtext: `[${this.layers[0].unit}/${this.productInfo.unit}]`
    };

    this.chart2Layer = {
      title: this.layers[1].label.replace(/\[.*?\]/g, ''),
      subtext: `[${this.layers[1].unit}/${this.productInfo.unit}]`
    };

    this.Chart1Options = this.getChartOptions(chart1Data);
    this.Chart2Options = this.getChartOptions(chart2Data);
  }

  private getChartOptions(data) {
    return {
      chart: {
        type: 'column',
        height: null,
        reflow: true
      },
      credits: {
        enabled: false
      },
      title: null,
      xAxis: {
        categories: ['Default', 'Optimized']
      },
      yAxis: {
        min: 0,
        title: {
          text: ''
        },
        gridLineWidth: 1,
        gridLineDashStyle: 'Dash',
        stackLabels: {
          format: '<span style="font-weight: 700;font-size: 20px;">{total:.2f}</span>',
          enabled: true
        }
      },
      legend: {
        enabled: false
      },
      tooltip: {
        distance: 25,
        headerFormat: '',
        style: {
          color: 'white',
          fontWeight: 'bold',
          fontSize: '14px',
          whiteSpace: 'normal'
        },
        pointFormat: '{series.name}: {point.y:.2f}',
        padding: 10,
        backgroundColor: '#4c4c4c',
        shadow: false,
        borderColor: '#4c4c4c',
        outside: true
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          pointWidth: 100,
          borderWidth: 0,
          dataLabels: {
            enabled: false
          },
          point: {
            events: {
              mouseOver() {
                this.graphic.attr({
                  fill: '#54a2a6'
                });
              },
              mouseOut() {
                this.graphic.attr({
                  fill: null
                });
              }
            }
          }
        },
        series: {
          states: {
            inactive: {
              enabled: false
            }
          }
        }
      },
      series: data
    } as Highcharts.Options;
  }

  private reflowCharts() {
    setTimeout(() => {
      this.chart1?.reflow();
      this.chart2?.reflow();
    }, 100);
  }
}
